'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronRight, X, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  FilterLockBadge,
  useLockHint,
  type LockTier,
} from './FilterLockBadge';

interface FilterGroupProps {
  /** Stable id used as the localStorage key in uncontrolled mode and as the
   *  ARIA-controls target. */
  id: string;
  icon: LucideIcon;
  label: string;
  /** Number of active filters inside this group. Surfaced on the collapsed
   *  header so users see what's narrowing results without expanding. */
  activeCount: number;
  /** Reset every filter inside this group. Shown as a small × next to the
   *  count when activeCount > 0. */
  onClear?: () => void;
  /** Initial open state for users who have no persisted preference yet.
   *  Defaults to false. Only honored in uncontrolled mode. */
  defaultOpen?: boolean;
  /** Controlled mode: parent owns the open state and gets every toggle. When
   *  set, internal-state + localStorage hydration is skipped. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Set when *every* control in this group is out of the visitor's tier.
   *  The header then carries the chip that says what unlocks it — but the
   *  group still opens: seeing which filters exist is the reason to sign up,
   *  so hiding them behind a shut header sells nothing. */
  lock?: LockTier | null;
  children: ReactNode;
}

const LS_PREFIX = 'fd:search-filter-group:';

/**
 * Collapsible filter group. Works both controlled (parent passes `open` +
 * `onOpenChange` to coordinate accordion behavior across siblings) and
 * uncontrolled (internal state, persisted per-id in localStorage).
 *
 * A group every one of whose filters is out of reach still opens — it just
 * wears a lock chip. The disabled controls inside are the pitch.
 *
 * When the group transitions from closed → open, its header is scrolled into
 * the nearest scroll container's viewport so the freshly-expanded content is
 * visible on mobile (where the old position can leave the content off-screen).
 */
export function FilterGroup({
  id,
  icon: Icon,
  label,
  activeCount,
  onClear,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  lock = null,
  children,
}: FilterGroupProps) {
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const { t } = useTranslation();
  const lockHint = useLockHint();
  const open = isControlled ? openProp : internalOpen;

  // Hydrate from localStorage after mount — only in uncontrolled mode.
  // Defer to an effect so SSR markup stays deterministic.
  useEffect(() => {
    if (isControlled) return;
    if (typeof window === 'undefined') return;
    const raw = window.localStorage.getItem(`${LS_PREFIX}${id}`);
    if (raw === 'open') setInternalOpen(true);
    else if (raw === 'closed') setInternalOpen(false);
  }, [id, isControlled]);

  const toggle = () => {
    const next = !open;
    if (isControlled) {
      onOpenChange?.(next);
    } else {
      setInternalOpen(next);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(
          `${LS_PREFIX}${id}`,
          next ? 'open' : 'closed',
        );
      }
    }
  };

  // Scroll the header into view on the closed → open transition. Using
  // requestAnimationFrame lets the expanded content render first so the
  // browser scrolls to the right final position.
  const headerRef = useRef<HTMLButtonElement>(null);
  const prevOpenRef = useRef(open);
  useEffect(() => {
    if (!prevOpenRef.current && open) {
      requestAnimationFrame(() => {
        headerRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }
    prevOpenRef.current = open;
  }, [open]);

  return (
    <div className="border-b border-border last:border-b-0 scroll-mt-2">
      <button
        ref={headerRef}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={`filter-group-${id}`}
        title={lock ? lockHint(lock, 'group') : undefined}
        className={cn(
          // 44px tap-height on mobile, tightened on desktop.
          'w-full flex items-center gap-2 px-1 min-h-11 lg:min-h-9 py-2',
          'text-left text-sm font-medium',
          'hover:bg-muted/40 transition-colors rounded-sm',
          lock && 'text-muted-foreground',
        )}
      >
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        {lock && <FilterLockBadge lock={lock} />}
        {/* A locked group's filters are stripped server-side, so a count
            here would claim a narrowing that did not happen. */}
        {!lock && activeCount > 0 && (
          <span className="inline-flex items-center gap-1 shrink-0">
            <span className="inline-flex min-w-[20px] h-[18px] items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-semibold px-1.5 leading-none">
              {activeCount}
            </span>
            {onClear && (
              <span
                role="button"
                tabIndex={0}
                aria-label={t('search.sidebar.clearGroup', {
                  defaultValue: 'Clear {{group}}',
                  group: label,
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  onClear();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onClear();
                  }
                }}
                // 32px hit area on mobile keeps it tappable without crowding
                // the chevron; 20px on desktop where pointer accuracy is high.
                className="inline-flex h-8 w-8 lg:h-5 lg:w-5 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer"
              >
                <X className="h-4 w-4 lg:h-3 lg:w-3" />
              </span>
            )}
          </span>
        )}
        <ChevronRight
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
            open && 'rotate-90',
          )}
        />
      </button>
      {open && (
        <div
          id={`filter-group-${id}`}
          className="pt-1 pb-3 px-1 space-y-3"
        >
          {children}
        </div>
      )}
    </div>
  );
}
